import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Bell,
  LogOut,
  Menu,
  X,
  Users,
  FileText,
  Gauge,
  Lock,
} from "lucide-react";
import logo from "../assets/primary_bg_removed.png";
import { useAuth } from "../context/AuthContext";

const AdminLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [visitedLinks, setVisitedLinks] = useState(
    new Set([location.pathname])
  );

  const handleLinkClick = (path) => {
    setIsSidebarOpen(false);
    setVisitedLinks((prev) => new Set([...prev, path]));
  };

  const handleLogout = () => {
    setIsSidebarOpen(false);
    logout();
    navigate("/login");
  };

  const getLinkStyle = (path) => {
    const isActive = location.pathname === path;
    const isVisited = visitedLinks.has(path);

    return {
      fontFamily: "Inter",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "24px",
      letterSpacing: "-0.015em",
      color: isActive ? "#0C60F2" : "#4C4D5E",
      backgroundColor: isActive ? "#F0F7FF" : "transparent",
      padding: "0.5rem 1rem",
      borderRadius: "0.375rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      textDecoration: isVisited ? "none" : "none",
      position: "relative",
      "&:hover": {
        backgroundColor: "#F0F7FF",
      },
      "&::after":
        isVisited && !isActive
          ? {
              content: '""',
              position: "absolute",
              bottom: "-2px",
              left: "0",
              width: "100%",
              height: "2px",
              backgroundColor: "#E2E8F0",
            }
          : {},
    };
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation Header */}
      <nav className="w-full bg-[#FAF9FF] p-4 flex justify-between items-center">
        <img src={logo} alt="Zeta Logo" className="w-32 h-auto" />
        <button
          className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        >
          <span className="sr-only">Open menu</span>
          <Menu className="h-6 w-6" />
        </button>
      </nav>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 bg-white w-64 p-4 transition-transform transform ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 md:relative md:w-64 md:flex-shrink-0`}
        >
          <button
            className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            onClick={() => setIsSidebarOpen(false)}
          >
            <span className="sr-only">Close menu</span>
            <X className="h-6 w-6" />
          </button>

          <nav className="space-y-2 mt-6">
            <Link
              to="/admin/dashboard"
              className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                location.pathname === "/admin/dashboard"
                  ? "bg-[#F0F7FF] text-[#0C60F2]"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => handleLinkClick("/admin/dashboard")}
            >
              <Home size={20} />
              <span>Dashboard</span>
            </Link>

            {user?.role === "superadmin" && (
              <Link
                to="/admin/users"
                className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                  location.pathname === "/admin/users"
                    ? "bg-[#F0F7FF] text-[#0C60F2]"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => handleLinkClick("/admin/users")}
              >
                <Users size={20} />
                <span>User Management</span>
              </Link>
            )}

            <Link
              to="/admin/notifications"
              className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                location.pathname === "/admin/notifications"
                  ? "bg-[#F0F7FF] text-[#0C60F2]"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => handleLinkClick("/admin/notifications")}
            >
              <Bell size={20} />
              <span>Send Notifications</span>
            </Link>

            <Link
              to="/admin/logs"
              className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                location.pathname === "/admin/logs"
                  ? "bg-[#F0F7FF] text-[#0C60F2]"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => handleLinkClick("/admin/logs")}
            >
              <FileText size={20} />
              <span>Logs</span>
            </Link>

            {/* Resource Monitor link */}
            <Link
              to="/admin/resouces-monitor"
              className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                location.pathname === "/admin/resouces-monitor"
                  ? "bg-[#F0F7FF] text-[#0C60F2]"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => handleLinkClick("/admin/resouces-monitor")}
            >
              <Gauge size={20} />
              <span>Server Resources</span>
            </Link>

            {/* Password Reset link */}
            <Link
              to="/admin/password-reset"
              className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${
                location.pathname === "/admin/password-reset"
                  ? "bg-[#F0F7FF] text-[#0C60F2]"
                  : "hover:bg-gray-50"
              }`}
              onClick={() => handleLinkClick("/admin/password-reset")}
            >
              <Lock size={20} />
              <span>Reset Password</span>
            </Link>

            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 p-2 rounded-md w-full text-left hover:bg-gray-50 transition-colors"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 p-6 bg-gray-100 transition-all duration-300 ${
            isSidebarOpen ? "ml-64" : "ml-0"
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

// import React, { useState } from "react";
// import { Link } from "react-router-dom";
// import { Home, Bell, LogIn, LogOut, Menu, X } from "lucide-react";
// import logo from "../assets/Primary.jpg";

// import { useAuth } from "../context/AuthContext";

// const AdminLayout = ({ children }) => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(false);

//   const { user, logout, isLoggedIn, setIsLoggedIn } = useAuth();

//   const styles = {
//     fontFamily: "Inter",
//     fontSize: "14px",
//     fontWeight: 600,
//     lineHeight: "24px",
//     letterSpacing: "-0.015em",
//     color: "#4C4D5E",
//   };

//   return (
//     <div className="min-h-screen flex flex-col">
//       {/* Navigation Header */}
//       <nav className="w-full bg-[#FAF9FF] p-4 flex justify-between items-center">
//         <img src={logo} alt="Zeta Logo" className="w-32 h-auto" />
//         <button
//           className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
//           onClick={() => setIsSidebarOpen(!isSidebarOpen)}
//         >
//           <span className="sr-only">Open menu</span>
//           <Menu className="h-6 w-6" />
//         </button>
//       </nav>

//       <div className="flex flex-1">
//         {/* Sidebar */}
//         <aside
//           className={`fixed inset-y-0 left-0 bg-white text-[#0C60F2] w-64 p-4 transition-transform transform ${
//             isSidebarOpen ? "translate-x-0" : "-translate-x-full"
//           } md:translate-x-0 md:relative md:w-64 md:flex-shrink-0`}
//         >
//           <button
//             className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
//             onClick={() => setIsSidebarOpen(false)}
//           >
//             <span className="sr-only">Close menu</span>
//             <X className="h-6 w-6" />
//           </button>
//           <nav className="space-y-4">
//             <Link
//               to="/dashboard"
//               className="flex items-center space-x-2"
//               style={styles}
//               onClick={() => setIsSidebarOpen(false)}
//             >
//               <Home size={20} />
//               <span>Dashboard</span>
//             </Link>
//             <Link
//               to="/notifications"
//               className="flex items-center space-x-2"
//               style={styles}
//               onClick={() => setIsSidebarOpen(false)}
//             >
//               <Bell size={20} />
//               <span>Send Notifications</span>
//             </Link>
//             <button
//               className="flex items-center space-x-2 w-full text-left"
//               style={styles}
//               onClick={() => {
//                 setIsSidebarOpen(false);
//                 isLoggedIn ? logout() : setIsLoggedIn(true);
//               }}
//             >
//               {isLoggedIn ? <LogOut size={20} /> : <LogIn size={20} />}
//               <span>{isLoggedIn ? "Logout" : "Login"}</span>
//             </button>
//           </nav>
//         </aside>

//         {/* Main Content */}
//         <main
//           className={`flex-1 p-6 bg-gray-100 transition-all duration-300 ${
//             isSidebarOpen ? "ml-64" : "ml-0"
//           }`}
//         >
//           {children}
//         </main>
//       </div>
//     </div>
//   );
// };

// export default AdminLayout;
